import { motion } from "framer-motion";

const DigitalWellnessTrends2024 = () => {
  return (
    <div className="prose prose-invert prose-lg max-w-none">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold text-white mb-6 mt-8 first:mt-0"
      >
        Digital Wellness Trends for 2024
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        As we navigate an increasingly digital world, the conversation around digital wellness continues to evolve. Here are the key trends shaping how we interact with technology in 2024.
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        1. Intentional Technology Use
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        The shift from "digital detox" to "intentional technology use" represents a more nuanced approach to our relationship with devices.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        Key Principles:
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Purposeful engagement</strong> rather than mindless scrolling
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Quality over quantity</strong> in digital interactions
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Mindful consumption</strong> of digital content
        </li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        2. Notification Management
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        With the average person receiving 63 notifications per day, managing these interruptions has become crucial for mental health and productivity.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        Emerging Solutions:
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Batching systems</strong> like Sera
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Smart filtering</strong> based on context and time
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Priority-based delivery</strong> systems
        </li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        3. Focus-First Design
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        App developers are increasingly prioritizing features that support deep work and sustained attention.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        Design Elements:
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Minimalist interfaces</strong> that reduce cognitive load
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Focus modes</strong> that limit distracting features
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Progress indicators</strong> that encourage completion
        </li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.4 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        4. Contextual Awareness
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Technology is becoming more aware of our context and adapting accordingly.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        Examples:
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.7 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Location-based settings</strong> that adjust based on where you are
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Time-sensitive features</strong> that respect your schedule
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Activity-based recommendations</strong> that align with your goals
        </li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        5. Community-Driven Wellness
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.9 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        Digital wellness is becoming a shared responsibility, with communities forming around healthy technology habits.
      </motion.p>

      <motion.h3
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.0 }}
        className="text-xl font-bold text-white mb-3 mt-6"
      >
        Community Features:
      </motion.h3>

      <motion.ul
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.1 }}
        className="list-disc list-inside text-slate-300 mb-4 space-y-2"
      >
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Shared challenges</strong> for reducing screen time
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Peer support</strong> for maintaining healthy habits
        </li>
        <li className="text-slate-300">
          <strong className="text-white font-semibold">Collective accountability</strong> for digital boundaries
        </li>
      </motion.ul>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.2 }}
        className="text-2xl font-bold text-white mb-4 mt-8"
      >
        Looking Ahead
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.3 }}
        className="text-slate-300 leading-relaxed mb-4"
      >
        The future of digital wellness lies in creating technology that serves us rather than distracts us. Tools like Sera are leading the way by putting user control and intentionality at the center of the experience.
      </motion.p>

      <motion.blockquote
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.4 }}
        className="border-l-4 border-teal-400 pl-4 my-6 italic text-slate-300"
      >
        What digital wellness trends are you most excited about? Share your thoughts with us on social media.
      </motion.blockquote>
    </div>
  );
};

export default DigitalWellnessTrends2024;
